import { Inject, Injectable, InjectionToken, ValueProvider } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { LogService } from '@rci/log-service';
import { Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';

const EXTERNAL_SCRIPT_LOAD_TIMEOUT = 3000;

export interface ExternalScript {
  url: string;
  isWaitUntilLoaded?: boolean;
}

export interface MiscServiceConfig {
  loaderFadeOutDelay: number;
}

export const MISC_SERVICE_CONFIG = new InjectionToken<MiscServiceConfig>('MiscServiceConfig');

@Injectable({
  providedIn: 'root',
})
export class MiscService {
  constructor(
    @Inject(DOCUMENT) private document: any,
    private logService: LogService,
  ) {}

  static configure(config?: MiscServiceConfig): ValueProvider {
    return {
      provide: MISC_SERVICE_CONFIG,
      useValue: config || {},
    };
  }

  // we need to wait for document.readyState to be 'complete' because dynatrace
  // script causes violations if injected before that - as it attempts to
  // document.write() if document is not ready and Chrome rejects that
  promiseDocumentReady = (): Promise<void> => {
    return new Promise(resolve => {
      const savedOnReadyStateChange = this.document.onreadystatechange;
      const resolveIfComplete = () => {
        if (this.document.readyState === 'complete') {
          this.document.onreadystatechange = savedOnReadyStateChange;
          resolve();
        }
      };
      this.document.onreadystatechange = resolveIfComplete;
      resolveIfComplete();
    });
  };

  injectExternalScript(externalScript: ExternalScript): Promise<any> {
    const script = this.document.createElement('script');
    script.src = externalScript.url;
    this.document.body.appendChild(script);
    return externalScript.isWaitUntilLoaded ? this.promiseScript(script) : Promise.resolve();
  }

  toObservable<T>(observableOrObject: Observable<T> | T): Observable<T> {
    return observableOrObject instanceof Observable ? observableOrObject : of(observableOrObject);
  }

  toPromise<T>(observableOrObject: Observable<T> | T): Promise<T> {
    return new Promise((resolve, reject) =>
      this.toObservable(observableOrObject).pipe(take(1)).subscribe({
        next: resolve,
        error: reject,
      }),
    );
  }

  private promiseScript(script) {
    return new Promise<void>(resolve => {
      const done = () => {
        script.onreadystatechange = null;
        script.onload = null;
        clearTimeout(timeout);
        resolve();
      };
      // if the script doesn't load for a while, we'll just continue without it
      const timeout = setTimeout(() => {
        this.logService.error('Could not load external script', script.src);
        done();
      }, EXTERNAL_SCRIPT_LOAD_TIMEOUT);
      if (!script.readyState) {
        script.onload = done;
        return;
      }
      // IE
      script.onreadystatechange = () => {
        if (script.readyState === 'loaded' || script.readyState === 'complete') {
          done();
        }
      };
    });
  }

  traverseAndFind(o: any, matchFn: (any) => boolean): any {
    if (matchFn(o)) {
      return o;
    }
    for (const key in o) {
      if (!o[key] || typeof o[key] !== 'object') {
        continue;
      }
      const result = this.traverseAndFind(o[key], matchFn);
      if (result) {
        return result;
      }
    }
  }
}
